import React from "react";
import { Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { NewsItem } from "./types";
import { newsCategories } from "./constants";

interface NewsCardProps {
  item: NewsItem;
}

const getColorClasses = (color: string) => {
  const colors = {
    blue: "bg-[#4A6FA5]/10 text-[#4A6FA5] border-[#4A6FA5]/20",
    purple: "bg-[#6B5B8D]/10 text-[#6B5B8D] border-[#6B5B8D]/20",
    green: "bg-[#2D8659]/10 text-[#2D8659] border-[#2D8659]/20",
    orange: "bg-[#B8860B]/10 text-[#B8860B] border-[#B8860B]/20",
    indigo: "bg-[#4A6FA5]/10 text-[#4A6FA5] border-[#4A6FA5]/20",
    teal: "bg-[#2D8659]/10 text-[#2D8659] border-[#2D8659]/20",
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

const getDetailPath = (item: NewsItem): string => {
  if (item.detailPath) return item.detailPath;
  if (item.link) return item.link;
  return `/news/${item.id}`;
};

const isExternalLink = (item: NewsItem): boolean => {
  return !!item.link && !item.detailPath;
};

const isInternalLink = (item: NewsItem): boolean => {
  return !!item.detailPath;
};

const NewsCard: React.FC<NewsCardProps> = ({ item }) => {
  const colorClasses = getColorClasses(item.color);
  const Icon = item.icon;
  const detailPath = getDetailPath(item);

  return (
    <article className="rounded-2xl border border-[#E8E6E1] bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 group">
      <div className="p-6">
        <div className="flex justify-between items-start mb-5">
          <div
            className={`w-12 h-12 rounded-xl ${colorClasses} flex items-center justify-center transition-transform group-hover:scale-110 duration-300 border`}
          >
            <Icon className="w-6 h-6" />
          </div>
          <span className="inline-block px-3 py-1 bg-[#F5F3EF] text-[#5A6577] text-xs font-medium rounded-full border border-[#E8E6E1]">
            {newsCategories.find((cat) => cat.id === item.category)?.label}
          </span>
        </div>

        <div className="flex items-center gap-2 text-[#7B8794] mb-3">
          <Calendar className="w-4 h-4" />
          <time className="text-sm">{item.date}</time>
        </div>

        <h3 className="text-lg font-bold text-[#2D3748] mb-3 line-clamp-2 group-hover:text-[#1B2A4A] transition-colors">
          {item.title}
        </h3>

        <p className="text-[#5A6577] text-sm mb-5 line-clamp-2 leading-relaxed text-justify">
          {item.excerpt}
        </p>

        <div className="flex flex-wrap gap-2 mb-5">
          {item.tags.map((tag, index) => (
            <span
              key={index}
              className={`px-2.5 py-0.5 text-xs rounded-full ${colorClasses} border`}
            >
              {tag}
            </span>
          ))}
        </div>

        {isInternalLink(item) ? (
          <Link
            to={detailPath}
            className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#A0750A] text-sm font-semibold transition-colors"
          >
            詳しく見る
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </Link>
        ) : isExternalLink(item) ? (
          <a
            href={detailPath}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#A0750A] text-sm font-semibold transition-colors"
          >
            詳しく見る
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </a>
        ) : (
          <button className="inline-flex items-center gap-2 text-[#B8860B] hover:text-[#A0750A] text-sm font-semibold transition-colors">
            詳しく見る
            <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </button>
        )}
      </div>
    </article>
  );
};

export default NewsCard;
